
import {Component, Vue, Watch} from "vue-property-decorator";
import {instituteAssignmentModule, instituteModule, rolesModule, usersModule} from "@/store";
import {MdbsCore} from "@/api/tsclient";
import {deepCopy} from "@/auxillary";
import TextField from "@/components/study/datafields/TextField.vue";
import {StringCompare} from "@/auxillary/sorting";
import IStudyInstituteShortDto = MdbsCore.IStudyInstituteShortDto;
import IShortUserDto = MdbsCore.IShortUserDto;
import IRoleDto = MdbsCore.IRoleDto;

@Component({
  components: {TextField},
})
export default class UserToInstituteAssignment extends Vue {

  private selectedUsersModel: string[] = new Array<string>();
  private searchModel: string = "";

  private assignedUsers: string[] = new Array<string>();
  private saving = false;
  private showSuccessMessage = false;

  get currentStudyInstitute(): IStudyInstituteShortDto {
    return instituteAssignmentModule.currentStudyInstitute;
  }

  @Watch('currentStudyInstitute', {immediate: true, deep: false})
  async handleSelectedStudyInstituteChanged(newInstitute: IStudyInstituteShortDto) {
    if (!newInstitute.instituteId) {
      return;
    }
    await this.initializeInstituteData(newInstitute.instituteId);
  }

  /**
   * Refresh institutes and get assigned users for the current institute
   *
   * @param instituteId
   */
  private async initializeInstituteData(instituteId: string) {
    const institute = await instituteModule.loadInstituteById(instituteId);
    const assignedUsers = deepCopy(institute?.users ?? []);
    this.selectedUsersModel.updateValues(assignedUsers);
    this.assignedUsers = assignedUsers;
  }

  get treeItems() {
    if (!instituteAssignmentModule.hasCurrentStudyInstitute) {
      return {};
    }

    const buildTreeItem = (id: string, name: string, children: any[]) => {
      return {id, name, children};
    };
    const buildUserLabel = (user: IShortUserDto) => `${user.userName} - ${user.displayName}`;
    const buildRoleTreeItem = (role: IRoleDto) => buildTreeItem(role.id, role.displayName ?? "", []);
    const buildUserTreeItem = (user: IShortUserDto) => buildTreeItem(user.id, buildUserLabel(user), []);

    return rolesModule
        .roles
        .map(buildRoleTreeItem)
        .map(roleItem => {
          roleItem.children = [...usersModule
              .users
              .filter(user => user.role?.id === roleItem.id)
              .map(buildUserTreeItem)];
          return roleItem;
        })
        .filter(roleItem => roleItem.children.length > 0)
        .sort((r1, r2) => StringCompare(r1.name, r2.name));
  }

  async handleSave() {
    const instituteId = instituteAssignmentModule.currentStudyInstitute.instituteId;
    if (!instituteId) {
      return;
    }

    try {
      this.saving = true;

      await instituteAssignmentModule.assignUsersToInstitute({
        instituteId: instituteId,
        userIds: this.selectedUsersModel
      });
      this.showSuccessMessage = true;
    } finally {
      this.saving = false;

      await this.initializeInstituteData(this.currentStudyInstitute.instituteId || "");
    }
  }

  get hasChanges(): boolean {
    return this.countChanges > 0;
  }

  get countChanges(): number {
    const addedSelectionsCount = this.selectedUsersModel.filter(su => !this.assignedUsers.some(au => au == su)).length;
    const removedSelectionCount = this.assignedUsers.filter(au => !this.selectedUsersModel.some(su => su == au)).length;

    return addedSelectionsCount + removedSelectionCount;
  }

  appendIcon(item: any, selected: boolean): any {
    const result = {icon: '', color: ''};
    if (item.children.length > 0) {
      return result;
    }
    const isInPreSelection = this.assignedUsers.includes(item.id);
    if (!isInPreSelection && selected) {
      result.icon = 'fa-plus-circle';
      result.color = 'green';
      return result;
    }
    if (isInPreSelection && !selected) {
      result.icon = 'fa-minus-circle';
      result.color = 'red';
      return result;
    }
    return result;
  }

  get loading() {
    return instituteModule.isLoading || usersModule.isLoading || instituteAssignmentModule.isLoading || rolesModule.isLoading;
  }
}

